import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import {
  MessageSquare, CheckCircle, ArrowRightLeft, Bot, Send,
  Image, Mic, FileText, ChevronDown, Plus, Search,
  User, Users, ArrowLeft, LogOut, Bell, MoreVertical,
  Paperclip, X
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { theme } from '../styles/theme';
import { Button, Spinner, ModalOverlay, ModalBox, SkeletonRow } from '../components/ui';
import api from '../services/api';
import { useQueueNotifier } from '../hooks/useQueueNotifier';
import { usePolling } from '../hooks/usePolling';
import { useWebSocket } from '../hooks/useWebSocket';
import { useToast } from '../components/Toast';
import NewChatDrawer from '../components/NewChatDrawer';

const CallCenterWorkspace = () => {
  const { t } = useTranslation('callcenter');
  const toast = useToast();
  const { tenantId } = useParams();
  const navigate = useNavigate();

  const [queue, setQueue] = useState([]);
  const [activeConversations, setActiveConversations] = useState([]);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState('');
  const [queueLoading, setQueueLoading] = useState(true);
  const [message, setMessage] = useState('');
  const [mediaUrls, setMediaUrls] = useState({});
  const [templates, setTemplates] = useState([]);
  const [showTemplates, setShowTemplates] = useState(false);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [transferAgents, setTransferAgents]       = useState([]);
  const [transferring, setTransferring]           = useState(false);
  const [showNewChat, setShowNewChat] = useState(false);
  const [mobileShowChat, setMobileShowChat] = useState(false);
  const [unreadCounts, setUnreadCounts] = useState({});
  const [attachedFile, setAttachedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const isAtBottomRef = useRef(true);
  const fileInputRef = useRef(null);

  const user = JSON.parse(localStorage.getItem('user') || '{}');

  // Verifica se uma sessão pertence ao atendente logado, comparando pelo
  // agent.id (humanAgentId cru). O humanAgentId pode estar em dois formatos:
  //   - email cru (ex: "mayara@clinica.com") — via captura/transferência manual
  //   - memberId sanitizado (ex: "mayara_clinica_com") — via handoff da IA
  // Normalizamos ambos os lados para comparar de forma robusta.
  const isMyHumanSession = useCallback((s) => {
    if (s?.agent?.type !== 'human') return false;
    // Compara pelo identificador cru do atendente (agent.id = humanAgentId).
    // Fallback para agent.name mantém compatibilidade com backend antigo (que
    // colocava o humanAgentId em name) durante a janela de deploy.
    const ident = s?.agent?.id || s?.agent?.name;
    if (!ident) return false;
    const email = (user.email || '').toLowerCase();
    const sub = user.sub || '';
    const sanitize = (v) => String(v).replace(/[^a-zA-Z0-9]/g, '_');
    const candidates = new Set(
      [email, sanitize(email), sub, sanitize(sub)].filter(Boolean)
    );
    const identLower = String(ident).toLowerCase();
    return candidates.has(identLower) || candidates.has(sanitize(identLower));
  }, [user.email, user.sub]);

  // ── WebSocket integration ──
  const selectedRef = useRef(null);
  useEffect(() => { selectedRef.current = selected; }, [selected]);

  const handleReconnect = useCallback(async () => {
    try {
      const [queueRes, activeRes] = await Promise.all([
        api.get(`/conversations/${tenantId}/queue`),
        api.get(`/conversations/${tenantId}/active`),
      ]);
      setQueue(queueRes.data.queue || []);

      const sessions = activeRes.data.conversations || [];

      const activeConvs = sessions
        .filter(isMyHumanSession)
        .map(s => ({
          id: s.id,
          customer: s.customer || t('customerFallback', { defaultValue: 'Usuário' }),
          phone: s.phone || '',
          messages: s.messages || [],
        }));
      setActiveConversations(activeConvs);
      setQueueLoading(false);
      return true;
    } catch {
      return false;
    }
  }, [tenantId, user.email, user.sub]); // eslint-disable-line

  const handleWsEvent = useCallback((data) => {
    switch (data.event) {
      case 'queue.item_added':
        setQueue(prev => {
          // Dedup por sessionId: o backend despacha queue.item_added de vários
          // pontos (tool-executor, message-processor, sessions) e pode repetir
          // para a mesma sessão; sem isso a conversa aparecia duplicada na fila.
          const item = {
            id: data.sessionId,
            customer: data.customer,
            phone: data.phone,
            preview: data.preview,
            waitTime: data.waitTime,
            connectorType: data.connectorType,
            group: data.group,
          };
          const idx = prev.findIndex(q => q.id === item.id);
          if (idx === -1) return [...prev, item];
          // Já existe: atualiza no lugar (preview/waitTime mais recentes).
          const next = [...prev];
          next[idx] = { ...next[idx], ...item };
          return next;
        });
        break;
      case 'queue.item_removed':
        setQueue(prev => prev.filter(item => item.id !== data.sessionId));
        break;
      case 'message.received': {
        const newMsg = {
          from: data.from,
          text: data.text,
          time: data.time,
          contentType: data.contentType,
          attachments: data.attachments,
        };
        // Evita duplicar o eco da própria resposta do atendente: quando ESTE atendente
        // envia, a UI já adiciona a mensagem localmente (otimista). O backend agora
        // reemite message.received (from: 'agent') para sincronizar outras abas — se a
        // última mensagem já for idêntica, ignoramos o eco.
        const isEchoOfLast = (msgs) => {
          const last = (msgs || [])[(msgs || []).length - 1];
          return last && last.from === newMsg.from && (last.text || '') === (newMsg.text || '');
        };
        let deduped = false;
        // Update messages for active conversations
        setActiveConversations(prev => prev.map(conv => {
          if (conv.id !== data.sessionId) return conv;
          if (isEchoOfLast(conv.messages)) { deduped = true; return conv; }
          return { ...conv, messages: [...(conv.messages || []), newMsg] };
        }));
        // Update selected if it's the current conversation
        setSelected(sel => {
          if (!sel || sel.id !== data.sessionId) return sel;
          if (isEchoOfLast(sel.messages)) { deduped = true; return sel; }
          return { ...sel, messages: [...(sel.messages || []), newMsg] };
        });
        if (deduped) break; // era só eco da própria resposta — não conta como não-lida
        // Increment unread counter for non-selected conversations or when not at scroll bottom.
        // Ecos da própria resposta do agente (from: 'agent') nunca contam como não-lida.
        if (newMsg.from !== 'agent') {
          setUnreadCounts(prev => {
            const sel = selectedRef.current;
            if (sel?.id === data.sessionId && isAtBottomRef.current) {
              return prev;
            }
            return { ...prev, [data.sessionId]: (prev[data.sessionId] || 0) + 1 };
          });
        }
        break;
      }
      case 'conversation.assigned':
        // New conversation assigned (transfer received)
        console.log('[WS Debug] conversation.assigned received:', data.sessionId, data.customer);
        setActiveConversations(prev => {
          if (prev.find(c => c.id === data.sessionId)) {
            // Already exists (self-capture) — update customer name if better
            return prev.map(c => c.id === data.sessionId
              ? { ...c, customer: data.customer || c.customer, phone: data.phone || c.phone, messages: (data.messages && data.messages.length > (c.messages || []).length) ? data.messages : c.messages, recentAttachments: data.recentAttachments || c.recentAttachments || [] }
              : c
            );
          }
          return [...prev, {
            id: data.sessionId,
            customer: data.customer,
            phone: data.phone,
            messages: data.messages || [],
            // Arquivos recebidos do cliente antes da transferência (ex: imagens que
            // um agente de IA sem visão apenas arquivou) — exibidos na galeria.
            recentAttachments: data.recentAttachments || [],
          }];
        });
        break;
      case 'conversation.transferred':
        // Conversation transferred away from this agent
        console.log('[WS Debug] conversation.transferred received:', data.sessionId, data.toAgentId || data.toAI);
        setActiveConversations(prev => prev.filter(c => c.id !== data.sessionId));
        setSelected(sel => sel?.id === data.sessionId ? null : sel);
        break;
      case 'conversation.resolved':
        // Conversation resolved
        console.log('[WS Debug] conversation.resolved received:', data.sessionId);
        setActiveConversations(prev => prev.filter(c => c.id !== data.sessionId));
        setSelected(sel => sel?.id === data.sessionId ? null : sel);
        break;
      default:
        break;
    }
  }, []);

  const token = sessionStorage.getItem('idToken') || localStorage.getItem('idToken') || '';
  const { status: wsStatus, send: wsSend, disconnect: wsDisconnect, pollingDisabled } = useWebSocket({
    tenantId,
    token,
    onEvent: handleWsEvent,
    groups: user.groups || [],
    onReconnect: handleReconnect,
  });

  // ── Debug WebSocket state ──────────────────────────────────────────────────
  useEffect(() => {
    console.log(`[WS Debug] status=${wsStatus}, pollingDisabled=${pollingDisabled}, token=${token ? 'present' : 'MISSING'}`);
  }, [wsStatus, pollingDisabled]); // eslint-disable-line react-hooks/exhaustive-deps

  const loadQueue = useCallback(async () => {
    try {
      const res = await api.get(`/conversations/${tenantId}/queue`);
      setQueue(res.data.queue || []);
      return true;
    } catch (e) { console.error(e); return false; }
    finally { setQueueLoading(false); }
  }, [tenantId]);

  const loadTemplates = async () => {
    try {
      const res = await api.get(`/templates/${tenantId}`);
      setTemplates(res.data.templates || []);
    } catch { }
  };

  useQueueNotifier(queue, () => {});

  usePolling(loadQueue, {
    baseInterval: 30000, // 30s: rede de segurança — o WebSocket cobre o tempo real
    enabled:      !pollingDisabled,
    backoff:      false,  // fila de atendimento — sem backoff, sempre atualizada
    pauseHidden:  true,   // pausa quando aba está inativa
  });

  // ── Reconciliação da fila (safety-net) ──────────────────────────────────────
  // Mesmo com o WebSocket conectado (pollingDisabled = true), a entrega de eventos
  // é best-effort: um queue.item_added/queue.item_removed pode ser perdido (drop de
  // conexão momentâneo, evento que chegou com a aba oculta, broadcast que correu com
  // o registro da conexão). Sem uma rede de segurança, a fila ficava desatualizada
  // até o atendente dar F5 — o que levava dois atendentes a capturar a mesma chamada.
  // Este poll leve de /queue sobrescreve a fila com o estado autoritativo do servidor
  // e roda SEMPRE, inclusive com o WS ativo, para que qualquer evento perdido se
  // autocorrija em poucos segundos. Intervalo relaxado (15s) para não pesar.
  usePolling(loadQueue, {
    baseInterval: 15000,
    enabled:      pollingDisabled, // só quando o polling principal está desligado (WS ativo)
    backoff:      false,
    pauseHidden:  true,
  });

  // Polling para atualizar mensagens das conversas ativas e detectar novas transferências
  const refreshActiveMessages = useCallback(async () => {
    try {
      const res = await api.get(`/conversations/${tenantId}/active`);
      const sessions = res.data.conversations || [];

      setActiveConversations(prev => {
        // Atualiza mensagens das conversas existentes
        const updated = prev.map(conv => {
          const session = sessions.find(s => s.id === conv.id);
          if (session) {
            // Update messages if backend has more
            const newMessages = session.messages && session.messages.length > (conv.messages || []).length
              ? session.messages : conv.messages;
            // Update customer name if backend has a better one (not generic)
            const betterCustomer = session.customer && session.customer !== 'Usuário' && session.customer !== conv.customer
              ? session.customer : conv.customer;
            return { ...conv, messages: newMessages, customer: betterCustomer, phone: session.phone || conv.phone };
          }
          return conv;
        });

        // Detecta novas conversas atribuídas a este atendente (transferências recebidas via polling fallback)
        // Only add if pollingDisabled is false (WS is not stable yet)
        const existingIds = new Set(prev.map(c => c.id));
        const newConvs = sessions
          .filter(s => isMyHumanSession(s) && !existingIds.has(s.id))
          .map(s => ({
            id: s.id,
            customer: s.customer || t('customerFallback', { defaultValue: 'Usuário' }),
            phone: s.phone || '',
            messages: s.messages || [],
          }));

        if (newConvs.length > 0) {
          return [...updated, ...newConvs];
        }
        return updated;
      });

      // Note: removal of conversations happens via WS events (conversation.resolved, conversation.transferred)
      // or when polling is active and user reloads the page

      // Atualiza selected se necessário
      if (selected?.id) {
        const session = sessions.find(s => s.id === selected.id);
        if (session && session.messages && session.messages.length > (selected.messages || []).length) {
          setSelected(sel => sel?.id === selected.id ? { ...sel, messages: session.messages } : sel);
        }
      }
    } catch (e) { /* silent */ }
  }, [tenantId, selected?.id, user.email, user.sub]); // eslint-disable-line

  usePolling(refreshActiveMessages, {
    baseInterval: 30000, // 30s: rede de segurança — o WebSocket entrega msgs em tempo real
    enabled:      !pollingDisabled,
    backoff:      false,
    pauseHidden:  true,
  });

  useEffect(() => {
    loadQueue();
    loadTemplates();
    // Reidrata as conversas ativas do atendente imediatamente no mount (F5).
    // Antes isso dependia só do WS reconectar ou do polling estar ativo, o que
    // criava uma corrida (pollingDisabled) e fazia conversas sumirem após refresh.
    handleReconnect();
  }, [tenantId]); // eslint-disable-line

  const loadMediaUrl = async (s3Key) => {
    if (!s3Key || mediaUrls[s3Key]) return;
    try {
      const res = await api.get(`/media/presigned?key=${encodeURIComponent(s3Key)}`);
      setMediaUrls(prev => ({ ...prev, [s3Key]: res.data.url }));
    } catch (e) { console.warn('media url error', e.message); }
  };

  const selectConversation = (conv) => {
    setSelected({ type: 'active', ...conv });
    setUnreadCounts(prev => ({ ...prev, [conv.id]: 0 }));
    setMobileShowChat(true);
  };

  const captureFromQueue = async (item) => {
    const u = JSON.parse(localStorage.getItem('user') || '{}');
    try {
      await api.post(`/conversations/${tenantId}/transfer`, {
        sessionId: item.id, targetAgentId: u.email || 'human', targetAgentType: 'human'
      });
    } catch (e) {
      if (e.response?.status === 409) {
        // Já capturada por outro atendente — remove da fila local e avisa o atendente,
        // para que a chamada não "suma" sem explicação (evita a sensação de bug).
        setQueue(prev => prev.filter(q => q.id !== item.id));
        toast.warning(
          t('alreadyCaptured', {
            customer: item.customer || t('thisCustomer', { defaultValue: 'este cliente' }),
            defaultValue: 'Esta chamada já foi capturada por outro atendente.',
          })
        );
        return;
      }
      console.warn(e.message);
      return;
    }

    // Fetch full conversation history from backend. O mesmo registro traz os
    // `recentAttachments` (arquivos recebidos do cliente) — preenchidos mesmo
    // quando o agente de IA não interpreta imagens, para o atendente que captura
    // ver o que foi enviado antes.
    let messages = [{ from: 'customer', text: item.preview, time: fmtTime() }];
    let recentAttachments = [];
    try {
      const res = await api.get(`/conversations/${tenantId}/active`);
      const session = (res.data.conversations || []).find(c => c.id === item.id);
      if (session && session.messages && session.messages.length > 0) {
        messages = session.messages;
      }
      recentAttachments = session?.recentAttachments || [];
    } catch (e) {
      // Fall back to single preview message if fetch fails
      console.warn('Failed to fetch conversation history:', e.message);
    }

    const conv = {
      id: item.id, customer: item.customer, phone: item.phone || '', email: '',
      messages,
      recentAttachments,
      // Contexto do handoff (Feature A) — leva o resumo/humor/motivo para o painel.
      summary: item.summary || null,
      reason: item.reason || null,
      sentiment: item.sentiment || null,
      moodReason: item.moodReason || null,
    };
    setActiveConversations(prev => [...prev, conv]);
    selectConversation(conv);
    setQueue(prev => prev.filter(q => q.id !== item.id));
  };

  const sendMessage = async () => {
    // If there's an attached file, send via media path
    if (attachedFile) { return sendMedia(); }
    if (!message.trim() || !selected || sending) return;
    const text = message.trim();
    setMessage('');
    if (selected.type === 'active') {
      try {
        await api.post(`/conversations/${tenantId}/reply`, { sessionId: selected.id, message: text });
        const newMsg = { from: 'agent', text, time: fmtTime() };
        const updated = { ...selected, messages: [...(selected.messages || []), newMsg] };
        setActiveConversations(prev => prev.map(c => c.id === selected.id ? updated : c));
        setSelected(updated);
        setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
      } catch (e) { console.error(e.message); }
    }
  };
  const handleMessageChange = (e) => {
    setMessage(e.target.value);
    e.target.style.height = 'auto';
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
  };

  const [sending] = useState(false);

  // ── File upload handlers ──
  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Limit: 16 MB
    if (file.size > 16 * 1024 * 1024) {
      alert(t('fileTooLarge', { defaultValue: 'Arquivo muito grande. Máximo: 16 MB.' }));
      return;
    }
    setAttachedFile(file);
    e.target.value = '';
  };

  const removeAttachedFile = () => setAttachedFile(null);

  const sendMedia = async () => {
    if (!attachedFile || !selected || uploading) return;
    setUploading(true);
    try {
      // 1. Get presigned upload URL
      const { data } = await api.post(`/conversations/${tenantId}/upload-media`, {
        sessionId: selected.id,
        filename: attachedFile.name,
        contentType: attachedFile.type || 'application/octet-stream'
      });

      // 2. Upload file directly to S3
      await fetch(data.uploadUrl, {
        method: 'PUT',
        headers: { 'Content-Type': attachedFile.type || 'application/octet-stream' },
        body: attachedFile
      });

      // 3. Send media reply
      await api.post(`/conversations/${tenantId}/reply-media`, {
        sessionId: selected.id,
        s3Key: data.s3Key,
        filename: data.filename,
        contentType: data.contentType,
        caption: message.trim() || ''
      });

      // 4. Update local UI
      const newMsg = {
        from: 'agent',
        text: message.trim() || `[${attachedFile.type?.startsWith('image/') ? t('mediaImage', { defaultValue: 'Imagem' }) : t('mediaFile', { defaultValue: 'Arquivo' })}]`,
        time: fmtTime(),
        attachments: [{ s3Key: data.s3Key, contentType: data.contentType, filename: data.filename }]
      };
      const updated = { ...selected, messages: [...(selected.messages || []), newMsg] };
      setActiveConversations(prev => prev.map(c => c.id === selected.id ? updated : c));
      setSelected(updated);
      setAttachedFile(null);
      setMessage('');
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    } catch (e) {
      console.error('sendMedia error:', e.message);
    } finally {
      setUploading(false);
    }
  };

  // ── Auto-scroll & scroll position tracking ──
  const handleMessagesScroll = useCallback(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    const atBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 40;
    isAtBottomRef.current = atBottom;
    // Clear unread count when user scrolls to bottom of selected conversation
    if (atBottom && selected?.id) {
      setUnreadCounts(prev => prev[selected.id] ? { ...prev, [selected.id]: 0 } : prev);
    }
  }, [selected?.id]);

  // Auto-scroll to bottom when new messages arrive for selected conversation
  useEffect(() => {
    if (selected && isAtBottomRef.current) {
      setTimeout(() => messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    }
  }, [selected?.messages?.length]); // eslint-disable-line

  const closeConversation = async () => {
    if (!selected) return;
    try { await api.post(`/conversations/${tenantId}/resolve`, { sessionId: selected.id }); } catch { }
    const remaining = activeConversations.filter(c => c.id !== selected.id);
    setActiveConversations(remaining);
    setSelected(remaining[0] ? { type: 'active', ...remaining[0] } : null);
    if (!remaining[0]) setMobileShowChat(false);
  };

  const openTransferModal = async () => {
    if (!selected) return;
    setShowTransferModal(true);
    // Recarrega se vazio ou se contém apenas o item de erro
    if (transferAgents.length && !transferAgents.some(a => a.type === 'error')) return;
    setTransferAgents([]); // reset para mostrar spinner
    try {
      const [marketRes, teamRes] = await Promise.all([
        api.get(`/marketplace/tenants/${tenantId}/agents`),
        api.get(`/teams/${tenantId}/members`),
      ]);
      const aiAgents    = (marketRes.data.agents  || []).map(a => ({ id: a.instanceId || a.agentId, name: `${a.instanceName || a.name} (IA)`, type: 'ai',    available: true }));
      const humanAgents = (teamRes.data.members   || []).map(m => ({ id: m.memberId, name: m.name, type: 'human', available: m.status === 'online' || m.status === 'active' || !m.status }));
      const all = [...aiAgents, ...humanAgents];
      setTransferAgents(all.length > 0 ? all : [{ id: 'empty', name: t('noAgentsFound', { defaultValue: 'Nenhum destino disponível' }), type: 'error', available: false }]);
    } catch (e) {
      console.error('openTransferModal error:', e?.response?.status, e?.message);
      setTransferAgents([{ id: 'unavailable', name: t('loadError', { defaultValue: 'Erro ao carregar destinos. Tente novamente.' }), type: 'error', available: false }]);
    }
  };

  const handleTransfer = async (target) => {
    if (!selected || transferring) return;
    setTransferring(true);
    try {
      await api.post(`/conversations/${tenantId}/transfer`, {
        sessionId: selected.id, targetAgentId: target.id, targetAgentType: target.type
      });
      setActiveConversations(prev => prev.filter(c => c.id !== selected.id));
      setSelected(null);
      setMobileShowChat(false);
      setShowTransferModal(false);
    } catch (e) { console.warn('transfer error', e.message); }
    finally { setTransferring(false); }
  };

  const renderMediaBubble = (msg) => {
    if (!msg.s3Key && !msg.attachments?.length) return null;
    const items = msg.attachments?.length
      ? msg.attachments
      : [{ s3Key: msg.s3Key, contentType: msg.contentType, filename: msg.filename }];
    return items.map((att, i) => {
      const url = mediaUrls[att.s3Key];
      if (!url) loadMediaUrl(att.s3Key);
      if (att.contentType?.startsWith('image/'))
        return url ? <MediaImg key={i} src={url} alt="" onClick={() => window.open(url, '_blank')} /> : <MediaPlaceholder key={i}><Image size={14} /> {t('loadingMedia')}</MediaPlaceholder>;
      if (att.contentType?.startsWith('audio/'))
        return url ? <audio key={i} controls src={url} style={{ maxWidth: '100%', marginTop: 4 }} /> : <MediaPlaceholder key={i}><Mic size={14} /> {t('loadingAudio')}</MediaPlaceholder>;
      return url ? <MediaLink key={i} href={url} target="_blank" rel="noreferrer"><FileText size={12} /> {att.filename || t('attachment')}</MediaLink> : <MediaPlaceholder key={i}><FileText size={14} /> {att.filename}</MediaPlaceholder>;
    });
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    localStorage.removeItem('idToken');
    navigate('/login');
  };

  // Lista unificada para sidebar: ativos + fila
  const sidebarItems = [
    ...activeConversations.map(c => ({ ...c, _kind: 'active' })),
    ...queue.map(q => ({ ...q, _kind: 'queue' })),
  ];

  const isSelected = (item) => selected?.id === item.id;

  // Considera urgente quando espera >= 10 minutos
  const isUrgentWait = (waitTime) => {
    if (!waitTime) return false;
    const h = parseInt(waitTime) || 0;
    if (waitTime.includes('h')) return true; // qualquer hora já é urgente
    return h >= 10;
  };

  const q = search.toLowerCase();
  const filteredQueue  = q ? queue.filter(i  => i.customer?.toLowerCase().includes(q) || i.phone?.toLowerCase().includes(q) || i.preview?.toLowerCase().includes(q)) : queue;
  const filteredActive = q ? activeConversations.filter(i => i.customer?.toLowerCase().includes(q) || i.phone?.toLowerCase().includes(q)) : activeConversations;

  return (
    <Shell>
      {/* ── SIDEBAR ── */}
      <Sidebar $hidden={mobileShowChat}>
        <SidebarHeader>
          <SidebarTitle>
            <Avatar $color={theme.colors.secondary}>{user.name?.charAt(0)?.toUpperCase() || '?'}</Avatar>
            <span style={{ fontSize: theme.typography.sm, fontWeight: theme.typography.semibold, color: theme.colors.textPrimary }}>
              {user.name}
            </span>
          </SidebarTitle>
          <SidebarActions>
            {queue.length > 0 && (
              <IconBtn title={t('queue')} onClick={() => {}}>
                <Bell size={18} />
                <NotifDot>{queue.length > 9 ? '9+' : queue.length}</NotifDot>
              </IconBtn>
            )}
            <IconBtn title={t('internalChatBtn')} onClick={() => setShowNewChat(true)}>
              <Plus size={20} />
            </IconBtn>
            <IconBtn title={t('common:logout')} onClick={handleLogout}>
              <LogOut size={18} />
            </IconBtn>
          </SidebarActions>
        </SidebarHeader>

        <SearchBar>
          <Search size={14} color={theme.colors.textMuted} />
          <SearchInput
            placeholder={t('searchPlaceholder', { defaultValue: 'Buscar por nome ou telefone...' })}
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <ClearSearch onClick={() => setSearch('')} title={t('common:clearFilters')}>×</ClearSearch>
          )}
        </SearchBar>

        <ConvList>
          {queueLoading ? (
            <>
              <SkeletonRow avatar lines={2} />
              <SkeletonRow avatar lines={2} />
              <SkeletonRow avatar lines={2} />
            </>
          ) : (
          <>
          {/* ── Fila de espera ── */}
          {filteredQueue.length > 0 && (
            <>
              <SectionHeader>
                <SectionLabel>{t('queueSection', { defaultValue: 'Fila de espera' })}</SectionLabel>
                <SectionCount $warn>{filteredQueue.length}</SectionCount>
              </SectionHeader>
              {filteredQueue.map(item => (
                <ConvRow
                  key={item.id}
                  $active={isSelected(item)}
                  $queue
                  onClick={() => captureFromQueue(item)}
                >
                  <ConvAvatar $queue>{item.customer?.charAt(0)?.toUpperCase() || '?'}</ConvAvatar>
                  <ConvInfo>
                    <ConvNameRow>
                      <ConvName>{item.customer}</ConvName>
                      {item.sentiment && (
                        <SentimentDot
                          $sentiment={item.sentiment}
                          title={item.moodReason || t(`sentiment.${item.sentiment}`, { defaultValue: item.sentiment })}
                        />
                      )}
                    </ConvNameRow>
                    {item.reason && <QueueReason title={item.reason}>{item.reason}</QueueReason>}
                    <ConvPreview>{item.summary || item.preview || t('waitingAttendance', { defaultValue: 'Aguardando atendimento' })}</ConvPreview>
                  </ConvInfo>
                  <QueueMeta>
                    <WaitTime $urgent={isUrgentWait(item.waitTime)}>
                      {item.waitTime ? `⏱ ${item.waitTime}` : ''}
                    </WaitTime>
                    <CaptureBtn>{t('capture', { defaultValue: 'Capturar' })}</CaptureBtn>
                  </QueueMeta>
                </ConvRow>
              ))}
            </>
          )}

          {/* ── Meus atendimentos ── */}
          {filteredActive.length > 0 && (
            <>
              <SectionHeader $mt={filteredQueue.length > 0}>
                <SectionLabel>{t('myConversations', { defaultValue: 'Meus atendimentos' })}</SectionLabel>
                <SectionCount>{filteredActive.length}</SectionCount>
              </SectionHeader>
              {filteredActive.map(item => (
                <ConvRow
                  key={item.id}
                  $active={isSelected(item)}
                  onClick={() => selectConversation(item)}
                >
                  <ConvAvatar>{item.customer?.charAt(0)?.toUpperCase() || '?'}</ConvAvatar>
                  <ConvInfo>
                    <ConvName>{item.customer}</ConvName>
                    <ConvPreview>{item.messages?.[item.messages.length - 1]?.text || ''}</ConvPreview>
                  </ConvInfo>
                  {unreadCounts[item.id] > 0 && (
                    <UnreadBadge>{unreadCounts[item.id] > 99 ? '99+' : unreadCounts[item.id]}</UnreadBadge>
                  )}
                </ConvRow>
              ))}
            </>
          )}

          {/* ── Vazio ── */}
          {filteredQueue.length === 0 && filteredActive.length === 0 && (
            <EmptyList>
              {search
                ? t('noSearchResults', { defaultValue: 'Nenhum resultado para "{{q}}"', q: search })
                : t('captureFromQueue')}
            </EmptyList>
          )}
          </>
          )}
        </ConvList>
      </Sidebar>

      {/* ── CHAT AREA ── */}
      <ChatPane $hidden={!mobileShowChat && !selected}>
        {selected ? (
          <>
            <ChatHeader>
              <BackBtn aria-label={t('common:back')} onClick={() => { setMobileShowChat(false); setSelected(null); }}>
                <ArrowLeft size={18} />
              </BackBtn>
              <ConvAvatar style={{ width: 36, height: 36, fontSize: 15 }}>
                {selected.customer?.charAt(0)?.toUpperCase() || '?'}
              </ConvAvatar>
              <ChatHeaderInfo>
                <ChatHeaderName>{selected.customer}</ChatHeaderName>
                <ChatHeaderSub>{selected.phone || selected.email || ''}</ChatHeaderSub>
              </ChatHeaderInfo>
              <ChatHeaderActions>
                <Button size="sm" variant="success" onClick={closeConversation}>
                  <CheckCircle size={14} /> {t('resolve')}
                </Button>
                <Button size="sm" variant="secondary" onClick={openTransferModal} title={t('transfer')}>
                  <ArrowRightLeft size={14} />
                </Button>
                <IconBtn onClick={() => setShowNewChat(true)} title={t('internalChatBtn')}>
                  <Bot size={18} />
                </IconBtn>
              </ChatHeaderActions>
            </ChatHeader>

            {/* Contexto do handoff (Feature A): resumo, motivo e humor do cliente.
                Só aparece quando a IA gerou o enriquecimento na transferência. */}
            {(selected.summary || selected.reason || selected.sentiment) && (
              <HandoffContext>
                <HandoffContextHeader>
                  <FileText size={13} />
                  {t('handoffContext', { defaultValue: 'Resumo do atendimento' })}
                  {selected.sentiment && (
                    <HandoffSentiment $sentiment={selected.sentiment}>
                      {t(`sentiment.${selected.sentiment}`, { defaultValue: selected.sentiment })}
                    </HandoffSentiment>
                  )}
                </HandoffContextHeader>
                {selected.reason && (
                  <HandoffLine>
                    <strong>{t('handoffReason', { defaultValue: 'Motivo' })}:</strong> {selected.reason}
                  </HandoffLine>
                )}
                {selected.summary && <HandoffLine>{selected.summary}</HandoffLine>}
                {selected.moodReason && (
                  <HandoffMood>{selected.moodReason}</HandoffMood>
                )}
              </HandoffContext>
            )}

            {/* Arquivos recebidos do cliente nesta conversa. O histórico de texto
                não guarda a mídia (sanitizada), então usamos `recentAttachments` —
                a trilha s3Key↔sessão preenchida mesmo quando o agente de IA não
                interpreta imagens. Permite ao atendente que recebe a transferência
                ver os exames/fotos/documentos enviados antes. */}
            {selected.recentAttachments?.length > 0 && (
              <ReceivedFiles>
                <ReceivedFilesHeader>
                  <Paperclip size={13} />
                  {t('receivedFiles', { defaultValue: 'Arquivos recebidos' })}
                  <ReceivedFilesCount>{selected.recentAttachments.length}</ReceivedFilesCount>
                </ReceivedFilesHeader>
                <ReceivedFilesGrid>
                  {selected.recentAttachments.map((att, i) => {
                    const url = mediaUrls[att.s3Key];
                    if (!url) loadMediaUrl(att.s3Key);
                    const label = att.filename || att.type || t('attachment');
                    if (att.contentType?.startsWith('image/')) {
                      return url
                        ? <MediaImg key={i} src={url} alt={label} title={label} onClick={() => window.open(url, '_blank')} />
                        : <MediaPlaceholder key={i}><Image size={14} /> {t('loadingMedia')}</MediaPlaceholder>;
                    }
                    if (att.contentType?.startsWith('audio/')) {
                      return url
                        ? <audio key={i} controls src={url} style={{ maxWidth: '100%' }} />
                        : <MediaPlaceholder key={i}><Mic size={14} /> {t('loadingAudio')}</MediaPlaceholder>;
                    }
                    return url
                      ? <MediaLink key={i} href={url} target="_blank" rel="noreferrer"><FileText size={12} /> {label}</MediaLink>
                      : <MediaPlaceholder key={i}><FileText size={14} /> {label}</MediaPlaceholder>;
                  })}
                </ReceivedFilesGrid>
              </ReceivedFiles>
            )}

            <Messages ref={messagesContainerRef} onScroll={handleMessagesScroll}>
              {(selected.messages || []).map((msg, idx) => (
                <Msg key={idx} $agent={msg.from === 'agent'}>
                  <Bubble $agent={msg.from === 'agent'}>
                    {msg.text}
                    {renderMediaBubble(msg)}
                  </Bubble>
                  <MsgTime>{msg.time ? new Date(msg.time).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : ''}</MsgTime>
                </Msg>
              ))}
              <div ref={messagesEndRef} />
            </Messages>

            {/* Templates */}
            {templates.length > 0 && (
              <TemplatesBar>
                <TemplatesToggle onClick={() => setShowTemplates(v => !v)}>
                  <Bot size={12} /> {t('quickReplies')}
                  <ChevronDown size={12} style={{ transform: showTemplates ? 'rotate(180deg)' : 'none', transition: '0.2s' }} />
                </TemplatesToggle>
                {showTemplates && (
                  <ChipList>
                    {templates.map(tpl => (
                      <Chip key={tpl.templateId} onClick={() => { setMessage(tpl.text); setShowTemplates(false); }}>
                        {tpl.title}
                      </Chip>
                    ))}
                  </ChipList>
                )}
              </TemplatesBar>
            )}

            {/* Attached file preview */}
            {attachedFile && (
              <AttachPreview>
                <AttachPreviewInfo>
                  {attachedFile.type?.startsWith('image/')
                    ? <Image size={14} />
                    : <FileText size={14} />}
                  <AttachFileName>{attachedFile.name}</AttachFileName>
                  <AttachFileSize>({(attachedFile.size / 1024).toFixed(0)} KB)</AttachFileSize>
                </AttachPreviewInfo>
                <AttachRemoveBtn onClick={removeAttachedFile} aria-label={t('removeFile', { defaultValue: 'Remover arquivo' })}>
                  <X size={14} />
                </AttachRemoveBtn>
              </AttachPreview>
            )}

            <InputRow>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*,application/pdf,.doc,.docx,.xls,.xlsx,.csv,.txt"
                style={{ display: 'none' }}
                onChange={handleFileSelect}
              />
              <AttachBtn
                aria-label={t('attachFile', { defaultValue: 'Anexar arquivo' })}
                onClick={() => fileInputRef.current?.click()}
                title={t('attachFile', { defaultValue: 'Anexar arquivo' })}
                disabled={uploading}
              >
                <Paperclip size={16} />
              </AttachBtn>
              <MsgInput
                value={message}
                onChange={handleMessageChange}
                onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                placeholder={attachedFile ? t('addCaption', { defaultValue: 'Adicionar legenda (opcional)...' }) : t('typeMessage')}
                rows={1}
                disabled={sending || uploading}
                enterKeyHint="send"
              />
              {templates.length > 0 && (
                <TemplateToggleBtn
                  aria-label="template-toggle"
                  data-testid="template-toggle"
                  onClick={() => setShowTemplates(v => !v)}
                  $active={showTemplates}
                  title={t('quickReplies', { defaultValue: 'Respostas rápidas' })}
                >
                  <FileText size={16} />
                </TemplateToggleBtn>
              )}
              <SendBtn aria-label={t('common:send')} onClick={sendMessage} disabled={(sending || uploading) || (!message.trim() && !attachedFile)}>
                {(sending || uploading) ? <Spinner size="16px" /> : <Send size={16} />}
              </SendBtn>
            </InputRow>
          </>
        ) : (
          <NothingSelected>
            <MessageSquare size={48} color={theme.colors.gray300} />
            <p>{t('captureFromQueue')}</p>
          </NothingSelected>
        )}
      </ChatPane>

      {/* ── NOVA CONVERSA DRAWER ── */}
      {showNewChat && (
        <NewChatDrawer
          tenantId={tenantId}
          currentUser={user}
          onClose={() => setShowNewChat(false)}
          onSelectChat={(conv) => {
            setSelected({ type: 'internal', ...conv });
            setMobileShowChat(true);
            setShowNewChat(false);
          }}
        />
      )}

      {/* ── MODAL DE TRANSFERÊNCIA ── */}
      {showTransferModal && selected && (
        <ModalOverlay onClick={() => setShowTransferModal(false)}>
          <ModalBox onClick={e => e.stopPropagation()}>
            <TransferHeader>
              <TransferTitle>{t('transferTitle', { defaultValue: 'Transferir conversa' })}</TransferTitle>
              <Button variant="ghost" size="sm" onClick={() => setShowTransferModal(false)}>×</Button>
            </TransferHeader>
            <TransferBody>
              <TransferClient>
                <TransferClientLabel>{t('client', { defaultValue: 'Cliente' })}</TransferClientLabel>
                <TransferClientName>{selected.customer}</TransferClientName>
              </TransferClient>
              <TransferSectionLabel>{t('selectDestination', { defaultValue: 'Selecione o destino' })}</TransferSectionLabel>
              {transferAgents.length === 0 && (
                <TransferEmpty><Spinner size="16px" /></TransferEmpty>
              )}
              {transferAgents.map(agent => (
                <TransferOption
                  key={agent.id}
                  $disabled={!agent.available}
                  onClick={() => agent.available && handleTransfer(agent)}
                >
                  {agent.type === 'ai' ? <Bot size={20} /> : <User size={20} />}
                  <TransferOptionInfo>
                    <TransferOptionName>{agent.name}</TransferOptionName>
                    <TransferOptionStatus $available={agent.available}>
                      {agent.available ? t('available', { defaultValue: 'Disponível' }) : t('busy', { defaultValue: 'Ocupado' })}
                    </TransferOptionStatus>
                  </TransferOptionInfo>
                  {agent.available && !transferring && (
                    <TransferOptionCta>{t('transfer', { defaultValue: 'Transferir' })}</TransferOptionCta>
                  )}
                </TransferOption>
              ))}
            </TransferBody>
          </ModalBox>
        </ModalOverlay>
      )}
    </Shell>
  );
};

// Retorna um timestamp ISO (não uma string já formatada). O renderizador das
// mensagens (linha ~775) faz `new Date(msg.time)` — se `time` for uma string já
// formatada como "14:32", `new Date("14:32")` é inválido e a bolha mostra
// "Invalid Date". Mantendo ISO aqui, o update otimista fica consistente com o
// que vem do backend (/active) e do WebSocket, que também são ISO.
function fmtTime(iso) {
  return new Date(iso || Date.now()).toISOString();
}

// ── STYLED ──────────────────────────────────────────────────────────────────

const Shell = styled.div`
  display: flex;
  height: 100dvh;
  background: ${theme.colors.bg};
  overflow: hidden;
  padding-top: env(safe-area-inset-top);
`;

const Sidebar = styled.div`
  width: 320px;
  min-width: 320px;
  display: flex;
  flex-direction: column;
  background: ${theme.colors.white};
  border-right: 1px solid ${theme.colors.border};
  @media (max-width: 767px) {
    width: 100%;
    min-width: 0;
    display: ${p => p.$hidden ? 'none' : 'flex'};
  }
`;

const SidebarHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: ${theme.colors.gray50};
  border-bottom: 1px solid ${theme.colors.border};
`;

const SidebarTitle = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const SidebarActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[1]};
`;

const IconBtn = styled.button`
  position: relative;
  background: none;
  border: none;
  cursor: pointer;
  color: ${theme.colors.textSecondary};
  width: 36px;
  height: 36px;
  border-radius: ${theme.radii.full};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: ${theme.transitions.fast};
  &:hover { background: ${theme.colors.gray100}; color: ${theme.colors.textPrimary}; }
`;

const NotifDot = styled.span`
  position: absolute;
  top: 2px; right: 2px;
  background: ${theme.colors.danger};
  color: white;
  font-size: 9px;
  font-weight: 700;
  border-radius: 999px;
  min-width: 14px;
  height: 14px;
  padding: 0 2px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SearchBar = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  background: ${theme.colors.gray50};
  border-bottom: 1px solid ${theme.colors.border};
`;

const SearchInput = styled.input`
  flex: 1;
  border: none;
  background: ${theme.colors.gray100};
  border-radius: ${theme.radii.full};
  padding: ${theme.spacing[1]} ${theme.spacing[3]};
  font-size: ${theme.typography.sm};
  outline: none;
  color: ${theme.colors.textPrimary};
  &::placeholder { color: ${theme.colors.textMuted}; }
`;

const ClearSearch = styled.button`
  background: none; border: none; cursor: pointer;
  color: ${theme.colors.textMuted}; font-size: 16px; line-height: 1;
  padding: 0 2px; flex-shrink: 0;
  &:hover { color: ${theme.colors.textPrimary}; }
`;

const ConvList = styled.div`
  flex: 1;
  overflow-y: auto;
`;

const SectionHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing[2]} ${theme.spacing[4]} ${theme.spacing[1]};
  margin-top: ${p => p.$mt ? theme.spacing[2] : '0'};
  border-top: ${p => p.$mt ? `1px solid ${theme.colors.border}` : 'none'};
`;

const SectionLabel = styled.div`
  font-size: 10px;
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textMuted};
  text-transform: uppercase;
  letter-spacing: 0.06em;
`;

const SectionCount = styled.div`
  min-width: 18px;
  height: 18px;
  border-radius: 9px;
  background: ${p => p.$warn ? theme.colors.danger : theme.colors.gray200};
  color: ${p => p.$warn ? 'white' : theme.colors.textMuted};
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
`;

const EmptyList = styled.div`
  padding: ${theme.spacing[8]} ${theme.spacing[4]};
  text-align: center;
  font-size: ${theme.typography.sm};
  color: ${theme.colors.textMuted};
`;

const ConvRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border-bottom: 1px solid ${theme.colors.gray50};
  cursor: pointer;
  background: ${p => p.$active ? theme.colors.primaryLight : p.$queue ? '#fffbeb' : 'transparent'};
  border-left: 3px solid ${p => p.$active ? theme.colors.primary : p.$queue ? theme.colors.warning : 'transparent'};
  transition: ${theme.transitions.fast};
  &:hover { background: ${p => p.$active ? theme.colors.primaryLight : p.$queue ? '#fef3c7' : theme.colors.gray50}; }
`;

const ConvAvatar = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  background: ${p => p.$queue ? theme.colors.warning : theme.colors.primary};
  color: white;
  font-size: 16px;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const QueueMeta = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
  flex-shrink: 0;
`;

const WaitTime = styled.div`
  font-size: 10px;
  font-weight: ${p => p.$urgent ? 700 : 400};
  color: ${p => p.$urgent ? theme.colors.danger : theme.colors.textMuted};
  white-space: nowrap;
`;

const CaptureBtn = styled.div`
  font-size: 10px;
  font-weight: ${theme.typography.semibold};
  color: white;
  background: ${theme.colors.warning};
  border-radius: ${theme.radii.full};
  padding: 2px 8px;
  white-space: nowrap;
`;

const Avatar = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: ${p => p.$color || theme.colors.primary};
  color: white;
  font-size: 13px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const ConvInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ConvName = styled.div`
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ConvNameRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
`;

// Mapa de cores por sentimento, reusado nos indicadores da fila e do cabeçalho.
const sentimentColor = (s) =>
  s === 'positive' ? theme.colors.success
    : s === 'negative' ? theme.colors.danger
      : theme.colors.warning;

const SentimentDot = styled.span`
  flex-shrink: 0;
  width: 8px;
  height: 8px;
  border-radius: ${theme.radii.full};
  background: ${p => sentimentColor(p.$sentiment)};
`;

const QueueReason = styled.div`
  font-size: 10px;
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textSecondary};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 1px;
`;

const HandoffContext = styled.div`
  background: ${theme.colors.infoLight};
  border-bottom: 1px solid ${theme.colors.border};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  font-size: ${theme.typography.xs};
  color: ${theme.colors.textSecondary};
`;

const HandoffContextHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary};
  margin-bottom: 4px;
`;

const HandoffSentiment = styled.span`
  margin-left: auto;
  font-size: 10px;
  font-weight: 600;
  text-transform: capitalize;
  color: ${p => sentimentColor(p.$sentiment)};
  border: 1px solid ${p => sentimentColor(p.$sentiment)};
  border-radius: ${theme.radii.full};
  padding: 1px 8px;
`;

const HandoffLine = styled.div`
  line-height: 1.5;
  margin-top: 2px;
  white-space: pre-wrap;
`;

const HandoffMood = styled.div`
  margin-top: 4px;
  font-style: italic;
  color: ${theme.colors.textMuted};
`;

const ConvPreview = styled.div`
  font-size: ${theme.typography.xs};
  color: ${theme.colors.textMuted};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  margin-top: 2px;
`;

const QueueBadge = styled.span`
  font-size: 10px;
  font-weight: 600;
  background: ${theme.colors.warningLight};
  color: #92400e;
  border: 1px solid ${theme.colors.warningBorder};
  border-radius: ${theme.radii.full};
  padding: 1px 6px;
  flex-shrink: 0;
`;

const UnreadBadge = styled.span`
  min-width: 20px;
  height: 20px;
  border-radius: 10px;
  background: ${theme.colors.primary};
  color: white;
  font-size: 10px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 5px;
  flex-shrink: 0;
`;

// ── CHAT PANE ────────────────────────────────────────────────────────────────

const ChatPane = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: ${theme.colors.bg};
  min-width: 0;
  @media (max-width: 767px) {
    display: ${p => p.$hidden ? 'none' : 'flex'};
    width: 100%;
  }
`;

const ChatHeader = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: ${theme.colors.white};
  border-bottom: 1px solid ${theme.colors.border};
  flex-shrink: 0;
`;

const BackBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${theme.colors.textSecondary};
  display: flex;
  align-items: center;
  padding: ${theme.spacing[1]};
  border-radius: ${theme.radii.md};
  &:hover { color: ${theme.colors.textPrimary}; }
  @media (min-width: 768px) { display: none; }
`;

const ChatHeaderInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const ChatHeaderName = styled.div`
  font-size: ${theme.typography.sm};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary};
`;

const ChatHeaderSub = styled.div`
  font-size: ${theme.typography.xs};
  color: ${theme.colors.textMuted};
`;

const ChatHeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  flex-shrink: 0;
  @media (max-width: 767px) {
    gap: ${theme.spacing[1]};
    & > button > span { display: none; }
  }
`;

const Messages = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${theme.spacing[4]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
  background: ${theme.colors.bg};
`;

const Msg = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${p => p.$agent ? 'flex-end' : 'flex-start'};
`;

const Bubble = styled.div`
  max-width: 72%;
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border-radius: ${p => p.$agent ? '12px 12px 2px 12px' : '12px 12px 12px 2px'};
  background: ${p => p.$agent ? theme.colors.primaryLight : theme.colors.white};
  color: ${theme.colors.textPrimary};
  font-size: ${theme.typography.sm};
  line-height: 1.5;
  box-shadow: 0 1px 2px rgb(0 0 0 / 0.06);
  border: 1px solid ${p => p.$agent ? theme.colors.primaryBorder : theme.colors.border};
  @media (max-width: 767px) { max-width: 85%; }
`;

const MsgTime = styled.div`
  font-size: 10px;
  color: ${theme.colors.textMuted};
  margin-top: 2px;
  padding: 0 4px;
`;

const TemplatesBar = styled.div`
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  background: ${theme.colors.white};
  border-top: 1px solid ${theme.colors.border};
`;

const TemplatesToggle = styled.button`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[1]};
  background: none;
  border: none;
  cursor: pointer;
  font-size: ${theme.typography.xs};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textSecondary};
  padding: 0;
  margin-bottom: ${theme.spacing[1]};
`;

const ChipList = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing[1]};
  margin-top: ${theme.spacing[1]};
`;

const Chip = styled.button`
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  min-height: 36px;
  background: ${theme.colors.white};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.full};
  font-size: ${theme.typography.xs};
  cursor: pointer;
  transition: ${theme.transitions.fast};
  &:hover { background: ${theme.colors.primary}; color: white; border-color: ${theme.colors.primary}; }
  @media (max-width: 767px) { min-height: 44px; }
`;

const InputRow = styled.div`
  display: flex;
  align-items: flex-end;
  gap: ${theme.spacing[2]};
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  padding-bottom: max(${theme.spacing[3]}, env(safe-area-inset-bottom));
  background: ${theme.colors.white};
  border-top: 1px solid ${theme.colors.border};
  flex-shrink: 0;
`;

const MsgInput = styled.textarea`
  flex: 1;
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  border: 1px solid ${theme.colors.border};
  border-radius: ${theme.radii.xl};
  font-size: ${theme.typography.sm};
  font-family: inherit;
  resize: none;
  min-height: 38px;
  max-height: 120px;
  line-height: 1.5;
  outline: none;
  background: ${theme.colors.gray50};
  &:focus { border-color: ${theme.colors.primary}; background: white; }
`;

const TemplateToggleBtn = styled.button`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: ${p => p.$active ? theme.colors.primaryLight : 'transparent'};
  color: ${p => p.$active ? theme.colors.primary : theme.colors.textSecondary};
  border: 1px solid ${p => p.$active ? theme.colors.primaryBorder : theme.colors.border};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: ${theme.transitions.fast};
  &:hover {
    background: ${theme.colors.primaryLight};
    color: ${theme.colors.primary};
    border-color: ${theme.colors.primaryBorder};
  }
`;

const SendBtn = styled.button`
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: ${theme.colors.primary};
  color: white;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: ${theme.transitions.fast};
  &:hover:not(:disabled) { background: ${theme.colors.primaryHover}; }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const AttachBtn = styled.button`
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: transparent;
  color: ${theme.colors.textSecondary};
  border: 1px solid ${theme.colors.border};
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: ${theme.transitions.fast};
  &:hover:not(:disabled) {
    background: ${theme.colors.primaryLight};
    color: ${theme.colors.primary};
    border-color: ${theme.colors.primaryBorder};
  }
  &:disabled { opacity: 0.4; cursor: not-allowed; }
`;

const AttachPreview = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  background: ${theme.colors.gray50};
  border-top: 1px solid ${theme.colors.border};
`;

const AttachPreviewInfo = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  color: ${theme.colors.textSecondary};
  font-size: ${theme.typography.xs};
`;

const AttachFileName = styled.span`
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary};
  max-width: 200px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const AttachFileSize = styled.span`
  color: ${theme.colors.textMuted};
`;

const AttachRemoveBtn = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  color: ${theme.colors.textMuted};
  padding: 4px;
  border-radius: ${theme.radii.full};
  display: flex;
  align-items: center;
  justify-content: center;
  &:hover { color: ${theme.colors.danger}; background: ${theme.colors.gray100}; }
`;

const NothingSelected = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing[3]};
  color: ${theme.colors.textMuted};
  font-size: ${theme.typography.sm};
`;

const MediaImg = styled.img`
  max-width: 200px;
  max-height: 160px;
  border-radius: ${theme.radii.md};
  margin-top: 4px;
  cursor: pointer;
  display: block;
`;

const MediaPlaceholder = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: ${theme.typography.xs};
  color: ${theme.colors.textMuted};
  margin-top: 4px;
`;

const MediaLink = styled.a`
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: ${theme.typography.xs};
  color: ${theme.colors.primary};
  margin-top: 4px;
  text-decoration: none;
  &:hover { text-decoration: underline; }
`;

const ReceivedFiles = styled.div`
  padding: ${theme.spacing[2]} ${theme.spacing[4]};
  border-bottom: 1px solid ${theme.colors.border};
  background: ${theme.colors.gray50};
`;

const ReceivedFilesHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: ${theme.typography.xs};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textSecondary};
  margin-bottom: ${theme.spacing[1]};
`;

const ReceivedFilesCount = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
  background: ${theme.colors.primary};
  color: #fff;
  font-size: 10px;
  font-weight: ${theme.typography.bold};
`;

const ReceivedFilesGrid = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${theme.spacing[2]};
  align-items: center;
`;

export default CallCenterWorkspace;

// ── MODAL DE TRANSFERÊNCIA ────────────────────────────────────────────────────

const TransferHeader = styled.div`
  display: flex; align-items: center; justify-content: space-between;
  padding: ${theme.spacing[4]} ${theme.spacing[5]};
  border-bottom: 1px solid ${theme.colors.border};
`;
const TransferTitle = styled.div`
  font-size: ${theme.typography.base};
  font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary};
`;
const TransferBody = styled.div`
  padding: ${theme.spacing[4]} ${theme.spacing[5]};
  display: flex; flex-direction: column; gap: ${theme.spacing[3]};
  max-height: 400px; overflow-y: auto;
`;
const TransferClient = styled.div`
  display: flex; align-items: center; gap: ${theme.spacing[2]};
  padding: ${theme.spacing[2]} ${theme.spacing[3]};
  background: ${theme.colors.gray50}; border-radius: ${theme.radii.md};
`;
const TransferClientLabel = styled.span`
  font-size: ${theme.typography.xs}; color: ${theme.colors.textMuted};
`;
const TransferClientName = styled.span`
  font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary};
`;
const TransferSectionLabel = styled.div`
  font-size: 10px; font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textMuted}; text-transform: uppercase; letter-spacing: 0.06em;
`;
const TransferEmpty = styled.div`
  display: flex; justify-content: center; padding: ${theme.spacing[4]};
`;
const TransferOption = styled.div`
  display: flex; align-items: center; gap: ${theme.spacing[3]};
  padding: ${theme.spacing[3]};
  border: 1.5px solid ${p => p.$disabled ? theme.colors.border : theme.colors.border};
  border-radius: ${theme.radii.lg};
  cursor: ${p => p.$disabled ? 'not-allowed' : 'pointer'};
  opacity: ${p => p.$disabled ? 0.5 : 1};
  transition: ${theme.transitions.fast};
  &:hover { ${p => !p.$disabled && `border-color: ${theme.colors.primary}; background: ${theme.colors.primaryLight};`} }
`;
const TransferOptionInfo = styled.div`flex: 1; min-width: 0;`;
const TransferOptionName = styled.div`
  font-size: ${theme.typography.sm}; font-weight: ${theme.typography.semibold};
  color: ${theme.colors.textPrimary};
`;
const TransferOptionStatus = styled.div`
  font-size: ${theme.typography.xs};
  color: ${p => p.$available ? theme.colors.success : theme.colors.danger};
  margin-top: 1px;
`;
const TransferOptionCta = styled.div`
  font-size: ${theme.typography.xs}; font-weight: ${theme.typography.semibold};
  color: ${theme.colors.primary}; white-space: nowrap;
`;
